/**
 * How much of the screen the open card gets (fix-74, Royce 2026-10-18). On a 2000 px screen the card shared the
 * width with the app nav and the queue, so the draft had a quarter of it. With a card open on a desktop the nav
 * becomes an icon rail and the queue a slim rail (Q brings it back); focus (F) hides both and moves the card's context
 * into a slide-over. Whether the card itself shows two columns is a container query in carddetail.css, not here.
 * Kept out of the components so it can be tested.
 */

export interface LayoutPrefs { queue: 'slim' | 'open'; focus: boolean }
export const DEFAULT_PREFS: LayoutPrefs = { queue: 'slim', focus: false };

export interface Layout {
  nav: 'full' | 'icons' | 'hidden';
  queue: 'full' | 'slim' | 'hidden';
  context: 'column' | 'slideover';
  focus: boolean;
}

export function deskLayout(o: { narrow: boolean; cardOpen: boolean; hasDraft: boolean; prefs: LayoutPrefs }): Layout {
  // The phone shows the list or the card, one at a time, and its own back button: unchanged.
  if (o.narrow || !o.cardOpen) return { nav: 'full', queue: 'full', context: 'column', focus: false };
  // Focus is about the draft; a card with no draft keeps its context, which is all it has.
  if (o.prefs.focus && o.hasDraft) return { nav: 'hidden', queue: 'hidden', context: 'slideover', focus: true };
  return { nav: 'icons', queue: o.prefs.queue === 'open' ? 'full' : 'slim', context: 'column', focus: false };
}

// ---- The choice, remembered per viewer ----

interface StoreLike { getItem(k: string): string | null; setItem(k: string, v: string): void }
const KEY = 'desk-layout';

/** The remembered choice, or the default. Never throws: storage can be missing, refused or hold anything. */
export function readPrefs(store: StoreLike | null): LayoutPrefs {
  if (!store) return DEFAULT_PREFS;
  try {
    const v = JSON.parse(store.getItem(KEY) ?? 'null');
    return {
      queue: v?.queue === 'open' ? 'open' : 'slim',
      focus: v?.focus === true,
    };
  } catch { return DEFAULT_PREFS; }
}

export function writePrefs(store: StoreLike | null, prefs: LayoutPrefs): void {
  if (!store) return;
  try { store.setItem(KEY, JSON.stringify(prefs)); } catch { /* storage refused: the choice lasts this screen */ }
}

// ---- Answer these first ----

/**
 * Whether "Answer these first" is shown in full or as one line. In full the first time the card is seen with a
 * question waiting; one line once every question is answered or once they have been seen (the draft was focused or
 * the card left), so the draft starts near the top. A click on the line overrides either way.
 */
export function answersOpen(o: { waiting: number; seen: boolean; toggled: boolean | null }): boolean {
  if (o.toggled !== null) return o.toggled;
  return o.waiting > 0 && !o.seen;
}

interface SessionLike { getItem(k: string): string | null; setItem(k: string, v: string): void }
const seenKey = (cardId: number) => `answers-seen:${cardId}`;

/** Whether this card's questions were seen before in this browser session. Never throws. */
export function answersSeen(store: SessionLike | null, cardId: number): boolean {
  try { return store?.getItem(seenKey(cardId)) === '1'; } catch { return false; }
}

export function markAnswersSeen(store: SessionLike | null, cardId: number): void {
  try { store?.setItem(seenKey(cardId), '1'); } catch { /* storage refused: shown in full again next time */ }
}

/** The step J (+1) or K (−1) takes through the bucket's list; null at either end or when the card is not in it. */
export function stepTo(ids: number[], current: number | null, dir: 1 | -1): number | null {
  const i = current === null ? -1 : ids.indexOf(current);
  if (i < 0) return ids.length ? ids[0] : null;
  return ids[i + dir] ?? null;
}
